/**
 * themeStore — preferencia de tema del usuario.
 *
 * 3 modos:
 *   - 'light': fuerza light mode
 *   - 'dark':  fuerza dark mode
 *   - 'system': sigue la preferencia del OS (default)
 *
 * Persiste en localStorage (clave `zen-theme`). Al cambiar, aplica la clase
 * `dark` al elemento <html> y responde a cambios de `prefers-color-scheme`
 * cuando el modo es 'system'.
 *
 * La inicialización debe hacerse ANTES de que React monte para evitar el
 * "flash of wrong theme" (FOWT). Ver main.tsx donde se llama a
 * `initializeTheme()`.
 */
import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

export type ThemeMode = 'light' | 'dark' | 'system'

interface ThemeState {
  mode: ThemeMode
  resolvedMode: 'light' | 'dark'
  setMode: (m: ThemeMode) => void
  /** Mostrar temas estacionales decorativos (Navidad, Fiestas Patrias). Default true. */
  seasonalEnabled: boolean
  setSeasonalEnabled: (v: boolean) => void
  /** IDs de seasonal themes que el usuario descartó este año. */
  dismissedSeasons: string[]
  dismissSeason: (id: string) => void
  clearDismissedSeasons: () => void
}

function resolveMode(mode: ThemeMode): 'light' | 'dark' {
  if (mode === 'system') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  }
  return mode
}

function applyToDOM(resolved: 'light' | 'dark') {
  const root = document.documentElement
  if (resolved === 'dark') {
    root.classList.add('dark')
  } else {
    root.classList.remove('dark')
  }
  root.dataset.theme = resolved
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set) => ({
      mode: 'system',
      resolvedMode: resolveMode('system'),
      setMode: (mode) => {
        const resolvedMode = resolveMode(mode)
        applyToDOM(resolvedMode)
        set({ mode, resolvedMode })
      },
      seasonalEnabled: true,
      setSeasonalEnabled: (seasonalEnabled) => set({ seasonalEnabled }),
      dismissedSeasons: [],
      dismissSeason: (id) => set(s => ({
        dismissedSeasons: s.dismissedSeasons.includes(id)
          ? s.dismissedSeasons
          : [...s.dismissedSeasons, id],
      })),
      clearDismissedSeasons: () => set({ dismissedSeasons: [] }),
    }),
    {
      name: 'zen-theme',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        mode: state.mode,
        seasonalEnabled: state.seasonalEnabled,
        dismissedSeasons: state.dismissedSeasons,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.resolvedMode = resolveMode(state.mode)
        }
      },
    },
  ),
)

/**
 * Aplica el tema al DOM antes del primer render de React.
 * Llamar en main.tsx ANTES de ReactDOM.render para evitar FOWT.
 */
export function initializeTheme() {
  let mode: ThemeMode = 'system'
  try {
    const raw = localStorage.getItem('zen-theme')
    if (raw) {
      const parsed = JSON.parse(raw)
      if (parsed?.state?.mode) mode = parsed.state.mode
    }
  } catch {
    // localStorage no disponible → usar system
  }
  applyToDOM(resolveMode(mode))

  // Si el modo es 'system', escuchar cambios del OS en tiempo real
  const mql = window.matchMedia('(prefers-color-scheme: dark)')
  const handler = () => {
    const current = useThemeStore.getState().mode
    if (current === 'system') {
      const resolved = resolveMode('system')
      applyToDOM(resolved)
      useThemeStore.setState({ resolvedMode: resolved })
    }
  }
  mql.addEventListener('change', handler)
}
